// src/hooks/useProductData.js
import { useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

export const useProductData = () => {
  const loadProductData = useCallback(async (productId, setFormData, setLoading) => {
    setLoading(true);
    try {
      // Fetch the product with price, new_price, and free_shipping
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();
      
      if (productError) throw productError;
      
      // Fetch product colors with their IDs, ordered by color_order
      const { data: colorData, error: colorError } = await supabase
        .from('product_colors')
        .select('id, color_id, product_id, color_order, is_home_color')
        .eq('product_id', productId)
        .order('color_order', { ascending: true });
      
      if (colorError) throw colorError;
      
      // Prepare array to hold complete color data
      const colorsWithDetails = [];
      
      // For each color, fetch sizes and images
      for (const color of colorData) {
        // Fetch sizes for this color (no longer including price fields)
        const { data: sizeData, error: sizeError } = await supabase
          .from('product_color_sizes')
          .select('id, size, stock')
          .eq('product_color_id', color.id);
          
        if (sizeError) throw sizeError;
        
        // Fetch images for this color, ordered by image_order
        const { data: imageData, error: imageError } = await supabase
          .from('product_color_images')
          .select('image_url, image_order')
          .eq('product_color_id', color.id)
          .order('image_order', { ascending: true });
          
        if (imageError) throw imageError;
        
        // Add complete color data to array
        colorsWithDetails.push({
          id: color.id,
          color_id: color.color_id,
          color_order: color.color_order,
          is_home_color: color.is_home_color,
          sizes: sizeData.map(size => ({
            id: size.id,
            size: size.size,
            stock: size.stock
          })),
          images: imageData.map(img => img.image_url)
        });
      }
      
      // Set form data with product-level pricing and free shipping
      setFormData({
        id: productData.id,
        name_ar: productData.name_ar,
        description_ar: productData.description_ar || '',
        category_id: productData.category_id,
        price: productData.price || '',
        new_price: productData.new_price || '',
        free_shipping: productData.free_shipping || false,
        colors: colorsWithDetails
      });
    } catch (error) {
      console.error('Error loading product data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getProductHomeColorImage = useCallback(async (productId) => {
    try {
      // Get the home color for this product
      const { data: homeColor, error: homeColorError } = await supabase
        .from('product_colors')
        .select('id')
        .eq('product_id', productId)
        .eq('is_home_color', true)
        .single();
      
      if (homeColorError) throw homeColorError;
      
      // Get the first image for the home color
      const { data: imageData, error: imageError } = await supabase
        .from('product_color_images')
        .select('image_url')
        .eq('product_color_id', homeColor.id)
        .order('image_order', { ascending: true })
        .limit(1)
        .single();
      
      if (imageError) throw imageError;
      
      return imageData.image_url;
    } catch (error) {
      console.error('Error getting home color image:', error);
      return null;
    }
  }, []);

  const getProductColorsWithImages = useCallback(async (productId) => {
    try {
      // Get all colors for this product, ordered by color_order
      const { data: colors, error: colorsError } = await supabase
        .from('product_colors')
        .select(`
          id, 
          color_id, 
          color_order, 
          is_home_color,
          colors!inner(name_ar, hex_code)
        `)
        .eq('product_id', productId)
        .order('color_order', { ascending: true });
      
      if (colorsError) throw colorsError;
      
      // Get images for each color
      const colorsWithImages = await Promise.all(
        colors.map(async (color) => {
          const { data: images, error: imagesError } = await supabase
            .from('product_color_images')
            .select('image_url, image_order')
            .eq('product_color_id', color.id)
            .order('image_order', { ascending: true });
          
          if (imagesError) throw imagesError;
          
          return {
            ...color,
            images: images || []
          };
        })
      );
      
      return colorsWithImages;
    } catch (error) {
      console.error('Error getting product colors with images:', error);
      return [];
    }
  }, []);

  const getProductWithPricing = useCallback(async (productId) => {
    try {
      // Get product with all colors and sizes (pricing and free shipping is now at product level)
      const { data: product, error: productError } = await supabase
        .from('products')
        .select(`
          *,
          product_colors (
            id,
            color_id,
            color_order,
            is_home_color,
            colors (name_ar, hex_code),
            product_color_sizes (
              id,
              size,
              stock
            ),
            product_color_images (
              image_url,
              image_order
            )
          )
        `)
        .eq('id', productId)
        .single();
      
      if (productError) throw productError;
      
      // Sort colors by color_order
      if (product.product_colors) {
        product.product_colors.sort((a, b) => a.color_order - b.color_order);
        
        // Sort images by image_order for each color
        product.product_colors.forEach(color => {
          if (color.product_color_images) {
            color.product_color_images.sort((a, b) => a.image_order - b.image_order);
          }
        });
      }
      
      return product;
    } catch (error) {
      console.error('Error getting product with pricing:', error);
      throw error;
    }
  }, []);

  return { 
    loadProductData, 
    getProductHomeColorImage, 
    getProductColorsWithImages,
    getProductWithPricing
  };
};
